import { cva } from "class-variance-authority";
import { cn } from "../../../client/cn";
import { UpArrow, DownArrow } from "../../../client/icons/icons-arrows";
import { SurfaceCard } from "../../../components/ui";

type MetricTrendTone = "primary" | "success" | "neutral";
type MetricTrendDirection = "up" | "down" | "none";

type AdminMetricCardTrend = {
  value: React.ReactNode;
  tone?: MetricTrendTone;
  direction?: MetricTrendDirection;
  hidden?: boolean;
  className?: string;
};

type AdminMetricCardProps = {
  icon: React.ReactNode;
  value: React.ReactNode;
  label: string;
  trend?: AdminMetricCardTrend;
  className?: string;
};

const metricCardVariants = cva("py-6 px-7.5");

const metricIconVariants = cva(
  "flex h-11.5 w-11.5 items-center justify-center rounded-full",
  {
    variants: {
      tone: {
        primary: "bg-primary-50 dark:bg-neutral-800",
      },
    },
    defaultVariants: {
      tone: "primary",
    },
  },
);

const metricTrendVariants = cva("flex items-center gap-1 text-sm font-medium", {
  variants: {
    tone: {
      primary: "text-primary",
      success: "text-success",
      neutral: "text-neutral-500 dark:text-neutral-400",
    },
  },
  defaultVariants: {
    tone: "primary",
  },
});

export function AdminMetricCard({
  icon,
  value,
  label,
  trend,
  className,
}: AdminMetricCardProps) {
  const trendDirection = trend?.direction ?? "none";
  const showTrend = Boolean(trend && !trend.hidden);
  const showTrendArrow = trendDirection !== "none";

  return (
    <SurfaceCard padding="none" className={cn(metricCardVariants(), className)}>
      <div className={metricIconVariants()}>{icon}</div>

      <div className="mt-4 flex items-end justify-between">
        <div>
          <h4 className="text-title-md font-bold text-black dark:text-white">
            {value}
          </h4>
          <span className="text-sm font-medium">{label}</span>
        </div>

        {showTrend ? (
          <span
            className={cn(
              metricTrendVariants({ tone: trend?.tone }),
              trend?.className,
            )}
          >
            {trend?.value}
            {showTrendArrow ? (
              trendDirection === "up" ? (
                <UpArrow />
              ) : (
                <DownArrow />
              )
            ) : null}
          </span>
        ) : null}
      </div>
    </SurfaceCard>
  );
}
